import { create } from "zustand";
import { ApiError, fetchJSON } from "@/lib/api";

type LikeToggleResponse = {
  productId: string;
  liked: boolean;
  likesCount: number;
};

type LikesState = {
  likedIds: Set<string>;
  counts: Record<string, number>;
  initialized: boolean;
  isLoading: boolean;
  hydrate: (authenticated: boolean) => Promise<void>;
  toggleLike: (productId: string) => Promise<LikeToggleResponse>;
  setCount: (productId: string, count: number) => void;
  reset: () => void;
};

export const useLikesStore = create<LikesState>((set, get) => ({
  likedIds: new Set(),
  counts: {},
  initialized: false,
  isLoading: false,
  hydrate: async (authenticated: boolean) => {
    if (!authenticated) {
      set({ likedIds: new Set(), initialized: true, isLoading: false });
      return;
    }
    set({ isLoading: true });
    try {
      const data = await fetchJSON<{
        items: { productId: string }[];
        total: number;
      }>("/likes/me?page=1&pageSize=200");
      const ids = new Set(data.items.map((i) => i.productId));
      set({ likedIds: ids, initialized: true, isLoading: false });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        set({ likedIds: new Set(), initialized: true, isLoading: false });
        return;
      }
      set({ initialized: true, isLoading: false });
    }
  },
  toggleLike: async (productId: string) => {
    const wasLiked = get().likedIds.has(productId);
    const optimistic = new Set(get().likedIds);
    if (wasLiked) optimistic.delete(productId);
    else optimistic.add(productId);
    set({ likedIds: optimistic });
    try {
      const res = await fetchJSON<LikeToggleResponse>(
        `/likes/${encodeURIComponent(productId)}`,
        { method: wasLiked ? "DELETE" : "POST" },
      );
      set((state) => ({
        likedIds: res.liked
          ? new Set([...state.likedIds, productId])
          : new Set([...state.likedIds].filter((id) => id !== productId)),
        counts: { ...state.counts, [productId]: res.likesCount },
      }));
      return res;
    } catch (err) {
      // rollback on failure
      set((state) => ({
        likedIds: wasLiked
          ? new Set([...state.likedIds, productId])
          : new Set([...state.likedIds].filter((id) => id !== productId)),
      }));
      throw err;
    }
  },
  setCount: (productId: string, count: number) =>
    set((state) => ({
      counts: { ...state.counts, [productId]: count },
    })),
  reset: () =>
    set({
      likedIds: new Set(),
      counts: {},
      initialized: false,
      isLoading: false,
    }),
}));
